import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import Layout from '../components/Layout/Layout';
import MeterReadingRequestsPanel from '../components/Admin/MeterReadingRequestsPanel';
import { useBetterAuthConvexAuth } from '../hooks/useAppAuth';
import { useSuperAdmin } from '../hooks/useSuperAdmin';
const DASHBOARD_LIST_LIMIT = 20;
const PAGE_SIZE = 5;

function paginate<T>(items: T[] | undefined, page: number, pageSize: number = PAGE_SIZE) {
  const safeItems = items ?? [];
  const totalItems = safeItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;

  return {
    items: safeItems.slice(start, end),
    totalItems,
    totalPages,
    currentPage,
    startIndex: totalItems === 0 ? 0 : start + 1,
    endIndex: Math.min(end, totalItems),
  };
}
const AdminDashboardPage: React.FC = () => {
  const { convexUser } = useBetterAuthConvexAuth();
  const navigate = useNavigate();
  const { isSuperAdmin } = useSuperAdmin();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [profilePage, setProfilePage] = useState(1);
  const [meterPage, setMeterPage] = useState(1);
  const [adminApprovalsPage, setAdminApprovalsPage] = useState(1);
  const [activeAnnouncementsPage, setActiveAnnouncementsPage] = useState(1);
  const [tempPasswordInfo, setTempPasswordInfo] = useState<{ username: string; password: string } | null>(null);
  const [tempPasswordCopied, setTempPasswordCopied] = useState(false);

  // Use Convex query for search
  const searchResultsQuery = useQuery(
    api.users.search,
    searchQuery.trim() ? { query: searchQuery.trim(), limit: 20 } : "skip"
  );
  const pendingMeterSubmissions = useQuery(api.meterSubmissions.listPending, { limit: DASHBOARD_LIST_LIMIT });
  const pendingProfileUpdates = useQuery(api.updateRequests.listPending, { limit: DASHBOARD_LIST_LIMIT });
  const pendingAdministrativeRequests = useQuery(
    api.administrativeRequests.listPendingAdministrativeRequests,
    isSuperAdmin ? { limit: DASHBOARD_LIST_LIMIT } : "skip"
  );
  const activeAnnouncements = useQuery(
    api.popups.listActiveAnnouncements,
    isSuperAdmin ? { limit: DASHBOARD_LIST_LIMIT } : "skip"
  );

  const profilePagination = paginate(pendingProfileUpdates, profilePage);
  const meterPagination = paginate(pendingMeterSubmissions, meterPage);
  const adminApprovalsPagination = paginate(pendingAdministrativeRequests, adminApprovalsPage);
  const activeAnnouncementsPagination = paginate(activeAnnouncements, activeAnnouncementsPage);

  const reviewAdministrativeRequestMutation = useMutation(api.administrativeRequests.reviewAdministrativeRequest);
  const updatePopupMutation = useMutation(api.popups.update);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setSearching(true);
    // The query will automatically run when searchQuery changes
    // Set searching to false after a short delay to show loading state
    setTimeout(() => setSearching(false), 500);
  };

  // Update search results when query returns
  React.useEffect(() => {
    if (searchResultsQuery !== undefined) {
      setSearchResults(searchResultsQuery);
      setSearching(false);
    }
  }, [searchResultsQuery]);

  const handleReviewAdministrativeRequest = async (
    requestId: any,
    decision: 'approved' | 'declined'
  ) => {
    try {
      const reviewerNotes = window.prompt(
        decision === 'approved' ? 'Optional approval notes' : 'Optional decline notes'
      );

      const result = await reviewAdministrativeRequestMutation({
        id: requestId,
        decision,
        reviewerNotes: reviewerNotes || undefined,
      });

      if (!result.success) {
        alert(result.error || 'Unable to process request');
      } else if (result.tempPassword) {
        setTempPasswordCopied(false);
        setTempPasswordInfo({
          username: result.tempPasswordFor || '',
          password: result.tempPassword,
        });
      }
    } catch (error: any) {
      console.error('Error reviewing administrative request:', error);
      alert(error.message || 'Unable to process request');
    }
  };

  const handleDeactivateAnnouncement = async (popupId: any) => {
    try {
      await updatePopupMutation({
        id: popupId,
        isActive: false,
      });
    } catch (error: any) {
      console.error('Error deactivating announcement:', error);
      alert(error.message || 'Unable to deactivate announcement');
    }
  };



  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-light">Welcome {convexUser?.name || 'Admin'}</h1>
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/admin/reports')}
              className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
            >
              Reports
            </button>
            {isSuperAdmin && (
              <>
                <button
                  onClick={() => navigate('/admin/manage-users')}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                >
                  Manage Users
                </button>
                <button
                  onClick={() => navigate('/admin/password-migration')}
                  className="bg-green-700 text-white px-4 py-2 rounded-md hover:bg-green-800"
                >
                  Password Migration
                </button>
              </>
            )}
          </div>
        </div>

        <h2 className="text-2xl font-medium font-sans mb-6">Admin Panel</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Find a customer */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-xl font-medium font-sans mb-4">Find a customer</h3>
            <form onSubmit={handleSearch}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter username, email, or name"
                className="w-full mb-4 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shade-green-500"
              />
              <button
                type="submit"
                disabled={searching}
                className="w-full bg-gray-800 text-white py-2 px-4 rounded-md hover:bg-gray-700 disabled:opacity-50"
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </form>

            {(searchResults.length > 0 && searchQuery.length > 0) && (
              <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
                {searchResults.map((result) => (
                  <div
                    key={result._id}
                    className="p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/admin/customer/${result._id}`)}
                  >
                    <div className="font-medium font-sans">{result.username}</div>
                    <td className=" whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-medium font-sans rounded-full ${result.role === 'super_admin'
                        ? 'bg-red-100 text-red-800'
                        : result.role === 'admin' ? 'bg-purple-100 text-purple-800'
                          : 'bg-green-100 text-green-800'
                        }`}>
                        {result.role === 'super_admin' ? 'Super Admin' : result.role === 'admin' ? 'Admin' : 'Customer'}
                      </span>
                    </td>
                    <div className="text-sm text-gray-600">{result.name || 'No name'}</div>
                    <div className="text-sm text-gray-600">{result.email || 'No email'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upload a Report - Note: File upload would need a Convex action */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-xl font-medium font-sans mb-4">Upload a Report</h3>
            <p className="text-gray-600 mb-4">
              CSV report upload functionality will be available through the Reports page.
            </p>
            <button
              onClick={() => navigate('/admin/reports')}
              className="w-full bg-gray-800 text-white py-2 px-4 rounded-md hover:bg-gray-700"
            >
              Go to Reports
            </button>
          </div>
        </div>
        {/* Pending Update Details */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-medium font-sans">Pending Profile Updates</h2>
              <p className="text-sm text-gray-600 mt-1">
                Customer profile updates waiting for admin approval.
              </p>
            </div>
            <div className="text-sm font-medium text-gray-500">
              {pendingProfileUpdates?.length ?? 0} pending
            </div>
          </div>

          {
            profilePagination.totalItems === 0 ? (
              <p className="text-gray-600">No pending submissions.</p>
            ) : (
              <div className="space-y-3">
                {profilePagination.items.map((submission: any) => (
                  <button
                    key={submission._id}
                    type="button"
                    onClick={() =>
                      navigate(`/admin/customer/${submission.userId}`, {
                        state: {
                          focusSection: "pending-profile-updates",
                          submissionId: submission._id,
                        },
                      })
                    }
                    className="w-full rounded-md border border-blue-400 bg-blue-50 px-4 py-3 text-left hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        {(submission?.requestedByUsername && submission.requestedByUsername) &&
                          <div className="font-semibold font-sans text-gray-900">
                            {submission?.requestedByUsername ? submission.requestedByUsername : 'No username'}:
                          </div>
                        }
                        {(submission?.changes?.name
                          && submission.changes.name) &&
                          <div className="text-sm text-gray-600 ">
                            {submission?.changes?.name
                              ? submission.changes.name
                              : 'Unknown customer'}
                          </div>}


                        {(submission?.changes?.address && submission.changes.address) &&
                          <div className="mt-2 text-sm text-gray-700">
                            Address: {submission?.changes?.address ? submission.changes.address : 'Not provided'}
                          </div>
                        }
                        {(submission?.changes?.email && submission.changes.email) &&
                          <div className="mt-2 text-sm text-gray-700">
                            Primary Email: {submission?.changes?.email ? submission.changes.email : 'Not provided'}
                          </div>
                        }
                        {(submission?.changes?.email2 && submission.changes.email2) &&
                          <div className="mt-2 text-sm text-gray-700">
                            Secondary Email: {submission?.changes?.email2 ? submission.changes.email2 : 'Not provided'}
                          </div>
                        }
                        {(submission?.changes?.mobileNumber && submission.changes.mobileNumber) &&
                          <div className="mt-2 text-sm text-gray-700">
                            Mobile: {submission?.changes?.mobileNumber ? submission.changes.mobileNumber : 'Not provided'}
                          </div>
                        }
                        {(submission?.changes?.telephoneNumber && submission.changes.telephoneNumber) &&
                          <div className="mt-2 text-sm text-gray-700">
                            Telephone: {submission?.changes?.telephoneNumber ? submission.changes.telephoneNumber : 'Not provided'}
                          </div>
                        }
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <div>{new Date(submission.updatedAt).toLocaleString('en-GB')}</div>
                        <div className="mt-2 uppercase tracking-wide">Expires At: {new Date(submission.tokenExpiresAt).toLocaleString('en-GB')}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )
          }

          {
            profilePagination.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t pt-3">
                <p className="text-sm text-gray-500">
                  Showing {profilePagination.startIndex}-{profilePagination.endIndex} of {profilePagination.totalItems}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                    disabled={profilePagination.currentPage === 1}
                    onClick={() => setProfilePage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {profilePagination.currentPage} of {profilePagination.totalPages}
                  </span>
                  <button
                    type="button"
                    className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                    disabled={profilePagination.currentPage === profilePagination.totalPages}
                    onClick={() => setProfilePage((p) => Math.min(profilePagination.totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )
          }

        </div>

        <MeterReadingRequestsPanel />

        {/* Pending Meter Submissions */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-medium font-sans">Pending Meter Submissions</h2>
              <p className="text-sm text-gray-600 mt-1">
                Customer photo uploads waiting for admin approval.
              </p>
            </div>
            <div className="text-sm font-medium text-gray-500">
              {pendingMeterSubmissions?.length ?? 0} pending
            </div>
          </div>

          {meterPagination.totalItems === 0 ? (
            <p className="text-gray-600">No pending submissions.</p>
          ) : (
            <div className="space-y-3">
              {meterPagination.items.map((submission: any) => (
                <button
                  key={submission._id}
                  type="button"
                  onClick={() => navigate(`/admin/customer/${submission.userId}`)}
                  className="w-full rounded-md border border-gray-200 px-4 py-3 text-left hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-medium font-sans text-gray-900">
                        {submission.user?.name || submission.user?.username || 'Unknown customer'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {submission.user?.username || 'No username'}
                      </div>
                      <div className="mt-2 text-sm text-gray-700">
                        Serial: {submission.submittedMeterSerialNumber || submission.extractedMeterSerialNumber || 'Not provided'}
                      </div>
                      <div className="text-sm text-gray-700">
                        Reading: {submission.submittedMeterReading || submission.extractedMeterReading || 'Not provided'}
                      </div>
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      <div>{new Date(submission.submittedAt).toLocaleString('en-GB')}</div>
                      <div className="mt-2 uppercase tracking-wide">{submission.source}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {meterPagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between border-t pt-3">
              <p className="text-sm text-gray-500">
                Showing {meterPagination.startIndex}-{meterPagination.endIndex} of {meterPagination.totalItems}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                  disabled={meterPagination.currentPage === 1}
                  onClick={() => setMeterPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {meterPagination.currentPage} of {meterPagination.totalPages}
                </span>
                <button
                  type="button"
                  className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                  disabled={meterPagination.currentPage === meterPagination.totalPages}
                  onClick={() => setMeterPage((p) => Math.min(meterPagination.totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {isSuperAdmin && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-12">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-medium font-sans">Pending Admin Approvals</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Admin and super-admin role assignments waiting for super-admin approval.
                </p>
              </div>
              <div className="text-sm font-medium text-gray-500">
                {pendingAdministrativeRequests?.length ?? 0} pending
              </div>
            </div>

            {!pendingAdministrativeRequests || adminApprovalsPagination.totalItems === 0 ? (
              <p className="text-gray-600">No pending admin requests.</p>
            ) : (
              <div className="space-y-3">
                {adminApprovalsPagination.items.map((request: any) => (
                  <div key={request._id} className="rounded-md border border-gray-200 px-4 py-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium font-sans text-gray-900">
                          {request.fullName || request.username}
                        </div>
                        <div className="text-sm text-gray-600">{request.username}</div>
                        <div className="text-sm text-gray-600">{request.email || 'No email'}</div>
                        <div className="mt-2 text-sm text-gray-700">
                          Requested Role: {request.requestedRole === 'super_admin' ? 'Super Admin' : 'Admin'}
                        </div>
                        <div className="text-sm text-gray-700">
                          Mobile: {request.mobileNumber || 'Not provided'}
                        </div>
                        <div className="text-sm text-gray-700">
                          Telephone: {request.telephoneNumber || 'Not provided'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">
                          {new Date(request.updatedAt).toLocaleString('en-GB')}
                        </div>
                        <div className="mt-3 flex gap-2">
                          <button
                            onClick={() => handleReviewAdministrativeRequest(request._id, 'approved')}
                            className="rounded-md bg-green-600 px-3 py-1 text-sm text-white hover:bg-green-700"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReviewAdministrativeRequest(request._id, 'declined')}
                            className="rounded-md bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700"
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {adminApprovalsPagination.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t pt-3">
                <p className="text-sm text-gray-500">
                  Showing {adminApprovalsPagination.startIndex}-{adminApprovalsPagination.endIndex} of {adminApprovalsPagination.totalItems}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                    disabled={adminApprovalsPagination.currentPage === 1}
                    onClick={() => setAdminApprovalsPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {adminApprovalsPagination.currentPage} of {adminApprovalsPagination.totalPages}
                  </span>
                  <button
                    type="button"
                    className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                    disabled={adminApprovalsPagination.currentPage === adminApprovalsPagination.totalPages}
                    onClick={() => setAdminApprovalsPage((p) => Math.min(adminApprovalsPagination.totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {isSuperAdmin && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-12">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-medium font-sans">Active Dashboard Announcements</h2>
                <p className="text-sm text-gray-600 mt-1">
                  These popups reappear on dashboard load until manually deactivated.
                </p>
              </div>
              <div className="text-sm font-medium text-gray-500">
                {activeAnnouncements?.length ?? 0} active
              </div>
            </div>

            {!activeAnnouncements || activeAnnouncementsPagination.totalItems === 0 ? (
              <p className="text-gray-600">No active announcements.</p>
            ) : (
              <div className="space-y-3">
                {activeAnnouncementsPagination.items.map((popup: any) => (
                  <div key={popup._id} className="rounded-md border border-gray-200 px-4 py-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium font-sans text-gray-900">{popup.title}</div>
                        <div className="text-sm text-gray-600">{popup.message}</div>
                        <div className="mt-2 text-sm text-gray-700">
                          Customer: {popup.user?.name || popup.user?.username || 'Unknown user'}
                        </div>
                        <div className="text-sm text-gray-700">
                          Phone: {popup.phoneNumber || 'Not provided'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">
                          {new Date(popup.updatedAt).toLocaleString('en-GB')}
                        </div>
                        <button
                          onClick={() => handleDeactivateAnnouncement(popup._id)}
                          className="mt-3 rounded-md bg-gray-800 px-3 py-1 text-sm text-white hover:bg-gray-700"
                        >
                          Deactivate
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {activeAnnouncementsPagination.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t pt-3">
                <p className="text-sm text-gray-500">
                  Showing {activeAnnouncementsPagination.startIndex}-{activeAnnouncementsPagination.endIndex} of {activeAnnouncementsPagination.totalItems}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                    disabled={activeAnnouncementsPagination.currentPage === 1}
                    onClick={() => setActiveAnnouncementsPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {activeAnnouncementsPagination.currentPage} of {activeAnnouncementsPagination.totalPages}
                  </span>
                  <button
                    type="button"
                    className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
                    disabled={activeAnnouncementsPagination.currentPage === activeAnnouncementsPagination.totalPages}
                    onClick={() => setActiveAnnouncementsPage((p) => Math.min(activeAnnouncementsPagination.totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}



        {/* Secret Analytics Link */}
        <div className="mt-8">
          <button
            onClick={() => navigate('/admin/secret-analytics')}
            className="bg-[#16A34A]  text-white px-6 py-3 rounded-md hover:bg-shade-green-700"
          >
            View Secret Interest Analytics
          </button>
        </div>

        {/* One-time temporary password for a newly approved admin */}
        {tempPasswordInfo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="temp-password-modal-title"
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            >
              <h2 id="temp-password-modal-title" className="text-lg font-semibold mb-2">
                Admin approved
              </h2>
              <p className="text-sm text-gray-700 mb-4">
                One-time temporary password for{' '}
                <span className="font-semibold">{tempPasswordInfo.username}</span>. It will{' '}
                <span className="font-semibold">not be shown again</span> — copy it now and share
                it securely. They must change it when they first log in.
              </p>
              <div className="flex items-center gap-2 mb-4">
                <code className="flex-1 bg-gray-100 border border-gray-300 rounded px-3 py-2 font-mono text-sm break-all select-all">
                  {tempPasswordInfo.password}
                </code>
                <button
                  autoFocus
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(tempPasswordInfo.password);
                      setTempPasswordCopied(true);
                    } catch {
                      alert('Copy failed — select the password text and copy it manually.');
                    }
                  }}
                  className="bg-[#16A34A] text-white px-3 py-2 rounded-md hover:bg-shade-green-700 text-sm whitespace-nowrap"
                >
                  {tempPasswordCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <button
                onClick={() => {
                  if (
                    tempPasswordCopied ||
                    window.confirm(
                      "You haven't copied the password. Close anyway? It cannot be recovered."
                    )
                  ) {
                    setTempPasswordInfo(null);
                    setTempPasswordCopied(false);
                  }
                }}
                className="w-full bg-gray-800 text-white px-4 py-2 rounded-md hover:bg-gray-900 text-sm"
              >
                I've saved the password — close
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminDashboardPage;
